<script lang="ts">
    import { isTouchDevice } from '@shared/device.browser.ts';
    import { viewportScrollObserver } from '@shared/scroll/ViewportScrollObserver.browser.ts';
    import type { Deal, SocialProof, TileImage } from '@shared/types/types';
    import DealBadge from '@tile/components/product-image/DealBadge.svelte';
    import type { ImageAspectRatio, ImageLoadingOptions } from '@tile/components/product-image/types';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';
    import ProductImage from '../product-image/ProductImage.svelte';

    interface Props {
        images: TileImage[];
        fallbackImage: TileImage;
        variationId: string;
        detailPageLink: string;
        imageLoadingOptions: ImageLoadingOptions;
        onClick?: (event: Event) => void;
        onImageChange?: (imagePosition: number) => void;
        deal?: Deal;
        aspectRatio?: ImageAspectRatio;
        rule?: string;
        brand?: string;
        size?: '50' | '100';
        title?: string;
        socialProof?: SocialProof;
        linkType?: 'image' | 'tile';
    }

    let {
        images,
        fallbackImage,
        variationId,
        detailPageLink,
        imageLoadingOptions,
        onClick,
        onImageChange,
        deal,
        aspectRatio,
        socialProof,
        rule,
        size = '100',
        brand = '',
        title = '',
        linkType = 'image',
    }: Props = $props();

    let _activeSlideIndex = $state(0);

    const isOnTouchDevice = isTouchDevice();

    /*                                                                     */
    /*                                                                                     */
    /*                   */
    let overflowBehavior = $state<'hidden' | 'unset'>('unset');

    function handleScrollActivityChange(isScrolling: boolean) {
        overflowBehavior = isScrolling ? 'hidden' : 'unset';
    }

    function scrollObserverAttachment(element: Element) {
        if (!isOnTouchDevice) return;
        return viewportScrollObserver.observe(handleScrollActivityChange)(element);
    }

    const mappedImages = $derived(
        images.length === 0
            ? [fallbackImage]
            : images.map(
                  (img): TileImage => ({
                      id: img.id ?? fallbackImage.id,
                      jpeg: img.jpeg ?? fallbackImage.jpeg,
                      webp: img.webp ?? fallbackImage.webp,
                      alt: img.alt ?? fallbackImage.alt,
                      fallback: img.fallback ?? fallbackImage.fallback,
                  }),
              ),
    );
</script>

{#key variationId}
    <div class="reptile-product-image-carousel__wrapper" {@attach scrollObserverAttachment}>
        <oc-carousel-v1
            class="reptile-product-image-carousel"
            style="

--overflow-behavior: {overflowBehavior}"
            oc-aria-label="Bildergalerie für {brand} {title}. Zum Wechseln der Ansicht Navigationselemente oder Wischgesten verwenden."
            hide-pagination-dots
            hide-arrow-buttons={isOnTouchDevice ? true : undefined}
            arrow-buttons-on-hover={isOnTouchDevice ? undefined : true}
            {size}
            onoc-item-visibility-changed={(e) => {
                const isScrolling = e.detail?.stageIsScrolling;
                const nextIndex = e.detail?.visibleItem?.index;

                if (isScrolling || typeof nextIndex !== 'number' || nextIndex === _activeSlideIndex) return;

                _activeSlideIndex = nextIndex;
                onImageChange?.(nextIndex);
            }}
        >
            {#each mappedImages as image, index}
                <ProductImage
                    {image}
                    imageLoadingOptions={index === 0
                        ? { fetchpriority: imageLoadingOptions.fetchpriority }
                        : { fetchpriority: 'low' }}
                    {variationId}
                    {detailPageLink}
                    {onClick}
                    {aspectRatio}
                    {linkType}
                />
            {/each}
        </oc-carousel-v1>
        {#if deal}
            <DealBadge {deal} {variationId} {rule} />
        {/if}
        {#if socialProof && Object.values(socialProof).some(Boolean)}
            <div class="reptile-product-image-carousel__social-proof-badges">
                <SocialProofBadges {socialProof} />
            </div>
        {/if}
    </div>
{/key}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image-carousel {
        &__wrapper {
            position: relative;
        }

        &__social-proof-badges {
            position: absolute;
            bottom: 0;
            left: 0;

            margin-bottom: tokens.$oc-semantic-spacing-25;
            margin-left: 10px;
        }
    }
</style>
